import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * First-run coach welcome — auto-plays once after a coach signs in.
 *
 * Five SVG-illustrated steps that walk through:
 *   1. Invite parents (the viral loop)
 *   2. Review shared journals + videos
 *   3. Assign drills as tasks
 *   4. Pro clip library
 *   5. Your roster leaderboard
 *
 * Persists `ui_flags.seen_coach_welcome=true` on dismiss so we don't
 * re-show it. The "Tour again" link in coach settings (future) can
 * unset it.
 *
 * Heavy lift in this file is illustration, not code — every step has
 * its own inline SVG that matches the brand gradient and the rest of
 * the app's visual language.
 */
const STEPS = [
  {
    title: 'Welcome, Coach',
    body: "You're set up. This 60-second tour shows the moves you'll use most.",
    illo: 'logo',
    cta: 'Start',
  },
  {
    title: 'Invite your parents',
    body: 'Type 10 phone numbers, hit send. Each parent gets an SMS that pre-binds them to your roster. No emails, no passwords.',
    illo: 'sms',
    cta: 'Next',
  },
  {
    title: 'Review what they share',
    body: 'When a parent shares a journal or video, it lands in your queue. Drop public feedback for the athlete + private notes for yourself.',
    illo: 'review',
    cta: 'Next',
  },
  {
    title: 'Assign drills',
    body: 'Pull a drill from the library or write your own. It shows up on the athlete\'s phone as a task with due dates.',
    illo: 'drills',
    cta: 'Next',
  },
  {
    title: 'Pro clips + roster board',
    body: 'Every kid gets a daily pro-clip push. Your roster board surfaces who\'s active, who\'s overtraining, and who just uploaded a video to review.',
    illo: 'roster',
    cta: 'Take me to the dashboard',
  },
];

export default function CoachWelcome() {
  const { user, setUiFlag } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [closing, setClosing] = useState(false);

  // Only show for accounts with role=coach who haven't seen this.
  const isCoach = user?.cricket_role === 'coach';
  const seen = user?.ui_flags?.seen_coach_welcome;
  const shouldShow = isCoach && !seen && !closing;

  useEffect(() => {
    if (!shouldShow) return;
    const onKey = (e) => {
      if (e.key === 'Escape') dismiss();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') setStep((s) => Math.max(0, s - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shouldShow, step]);

  if (!shouldShow) return null;

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const next = () => {
    if (isLast) dismiss();
    else setStep((s) => s + 1);
  };

  const dismiss = async () => {
    setClosing(true);
    await setUiFlag('seen_coach_welcome', true);
    if (isLast) {
      // No navigation needed if already on coach dashboard.
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
    >
      <div
        className="rounded-3xl overflow-hidden w-full max-w-md"
        style={{
          background: '#0a0e27', color: '#f7fafc',
          border: '1px solid rgba(0,201,167,0.3)',
        }}
      >
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(0,166,81,0.25), rgba(0,217,255,0.20))',
            padding: 24, paddingBottom: 32,
          }}
        >
          <Illo kind={current.illo} />
        </div>

        <div className="p-6">
          <Stepper count={STEPS.length} current={step} />
          <h2 className="text-2xl font-extrabold tracking-tight mt-4">{current.title}</h2>
          <p className="opacity-80 mt-2 text-sm leading-relaxed">{current.body}</p>

          <div className="flex items-center justify-between mt-6">
            <button
              onClick={dismiss}
              className="text-sm opacity-60 hover:opacity-100"
            >
              Skip
            </button>
            <button
              onClick={next}
              className="px-4 py-2.5 rounded-lg text-sm font-semibold text-white"
              style={{ background: 'linear-gradient(135deg, #00A651, #00D9FF)' }}
            >
              {current.cta} →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stepper({ count, current }) {
  return (
    <div className="flex gap-1.5">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="h-1 flex-1 rounded-full"
          style={{
            background: i <= current
              ? 'linear-gradient(90deg, #00A651, #00D9FF)'
              : 'rgba(150,160,180,0.2)',
            transition: 'all 0.2s',
          }}
        />
      ))}
    </div>
  );
}

function Illo({ kind }) {
  const size = 220;
  switch (kind) {
    case 'logo':
      return (
        <svg width={size} height={size} viewBox="0 0 64 64" style={{ margin: '0 auto', display: 'block' }}>
          <defs>
            <linearGradient id="cw-arc" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#00A651" />
              <stop offset="100%" stopColor="#00D9FF" />
            </linearGradient>
          </defs>
          <path d="M 52 18 A 24 24 0 1 1 32 8" fill="none"
                stroke="url(#cw-arc)" strokeWidth="6" strokeLinecap="round" />
          <path d="M 30 2 L 32 8 L 26 11 Z" fill="url(#cw-arc)" />
          <circle cx="42" cy="42" r="7.2" fill="#FF4F2B" />
        </svg>
      );
    case 'sms':
      return (
        <svg width={size} height={size} viewBox="0 0 220 220" style={{ display: 'block', margin: '0 auto' }}>
          <defs>
            <linearGradient id="cw-sms" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#00A651" />
              <stop offset="100%" stopColor="#00D9FF" />
            </linearGradient>
          </defs>
          {/* phone outline */}
          <rect x="65" y="22" width="90" height="170" rx="14" fill="#1e2a44" stroke="rgba(255,255,255,0.15)" />
          {/* sms bubbles */}
          <rect x="78" y="50" width="64" height="22" rx="11" fill="rgba(0,201,167,0.25)" />
          <text x="86" y="65" fill="#00C9A7" fontSize="11" fontWeight="800">+1 415…</text>
          <rect x="78" y="80" width="64" height="22" rx="11" fill="rgba(0,201,167,0.25)" />
          <text x="86" y="95" fill="#00C9A7" fontSize="11" fontWeight="800">+1 408…</text>
          <rect x="78" y="110" width="64" height="22" rx="11" fill="rgba(0,201,167,0.25)" />
          <text x="86" y="125" fill="#00C9A7" fontSize="11" fontWeight="800">+1 650…</text>
          {/* send button */}
          <rect x="78" y="148" width="64" height="32" rx="8" fill="url(#cw-sms)" />
          <text x="110" y="168" fill="#060818" fontSize="11" fontWeight="800" textAnchor="middle">SEND ↗</text>
        </svg>
      );
    case 'review':
      return (
        <svg width={size} height={size} viewBox="0 0 220 220" style={{ display: 'block', margin: '0 auto' }}>
          <defs>
            <linearGradient id="cw-review" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#00A651" />
              <stop offset="100%" stopColor="#00D9FF" />
            </linearGradient>
          </defs>
          <rect x="30" y="40" width="160" height="100" rx="10" fill="#000" />
          <polygon points="100,75 130,90 100,105" fill="url(#cw-review)" />
          <rect x="30" y="150" width="120" height="10" rx="5" fill="rgba(255,255,255,0.2)" />
          <rect x="30" y="165" width="80" height="10" rx="5" fill="rgba(0,201,167,0.4)" />
          <circle cx="180" cy="160" r="14" fill="url(#cw-review)" />
          <text x="180" y="165" textAnchor="middle" fill="#060818" fontSize="14" fontWeight="900">✓</text>
        </svg>
      );
    case 'drills':
      return (
        <svg width={size} height={size} viewBox="0 0 220 220" style={{ display: 'block', margin: '0 auto' }}>
          <defs>
            <linearGradient id="cw-drill" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#00A651" />
              <stop offset="100%" stopColor="#00D9FF" />
            </linearGradient>
          </defs>
          {[0, 1, 2].map((i) => (
            <g key={i}>
              <rect x="30" y={40 + i * 50} width="160" height="36" rx="8" fill="rgba(255,255,255,0.04)" stroke="rgba(0,201,167,0.3)" />
              <circle cx="48" cy={58 + i * 50} r="8" fill="url(#cw-drill)" />
              <text x="48" y={63 + i * 50} textAnchor="middle" fill="#060818" fontWeight="900" fontSize="11">{i + 1}</text>
              <rect x="64" y={50 + i * 50} width="110" height="6" rx="3" fill="rgba(255,255,255,0.4)" />
              <rect x="64" y={62 + i * 50} width="70" height="5" rx="2.5" fill="rgba(255,255,255,0.2)" />
            </g>
          ))}
        </svg>
      );
    case 'roster':
      return (
        <svg width={size} height={size} viewBox="0 0 220 220" style={{ display: 'block', margin: '0 auto' }}>
          <defs>
            <linearGradient id="cw-roster" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#00A651" />
              <stop offset="100%" stopColor="#00D9FF" />
            </linearGradient>
          </defs>
          {[0, 1, 2, 3].map((i) => (
            <g key={i}>
              <circle cx={40 + i * 50} cy="90" r="20" fill="url(#cw-roster)" opacity={0.4 + i * 0.15} />
              <rect x={30 + i * 50} y="118" width="20" height="4" rx="2" fill="rgba(255,255,255,0.4)" />
              <text x={40 + i * 50} y="140" fill="#fbbf24" fontSize="14" textAnchor="middle">🔥</text>
              <text x={40 + i * 50} y="158" fill="rgba(255,255,255,0.7)" fontSize="11" fontWeight="800" textAnchor="middle">{(7 - i)}d</text>
            </g>
          ))}
        </svg>
      );
    default:
      return null;
  }
}
